import React, { useEffect, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { ShareRoutes } from './navigation_logic';
import type { ShareSurface } from './types';

interface NearestDestinationRedirectProps {
  /** The surface asking — the one whose URL the sender ends up on. */
  surface: ShareSurface;
}

/**
 * Send a URL naming a destination this surface does not render to the one that
 * stands for it. Each router mounts this on the segments only the other
 * surface has, so a session started under one flag setting resumes under the
 * other instead of landing on an outlet with nothing to show.
 *
 * Which surface is asking is settled before either router mounts, in
 * `ShareFlowGate`, so this never has to wonder whether the flag has arrived:
 * a garden URL only ever reaches the wizard's fallback when the flag really
 * is off.
 *
 * The target is resolved in an effect and the redirect rendered on the pass
 * after, the same shape `ShareWizardRedirect` has. Rendering the `<Redirect>`
 * in the first pass navigates out of a subtree React is still mounting — on a
 * cold load this whole outlet mounts in one concurrent render, and the
 * reported recoverable error ("there was an error during concurrent rendering
 * but React was able to recover") fails an e2e run even though React retries
 * and lands correctly. Nothing is on screen while it waits: this component
 * only ever renders a redirect.
 */
const NearestDestinationRedirect: React.FC<NearestDestinationRedirectProps> = ({
  surface,
}) => {
  const { topicId } = useParams<{ topicId: string }>();
  const { pathname, search } = useLocation();
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    const here = ShareRoutes[surface];
    const other = ShareRoutes[surface === 'garden' ? 'wizard' : 'garden'];
    setRedirectTo(
      here.pathFor(
        topicId,
        here.nearest(other.destinationFromPath(pathname)),
        search
      )
    );
  }, [surface, topicId, pathname, search]);

  if (!redirectTo) return null;

  return <Redirect to={redirectTo} />;
};

export default NearestDestinationRedirect;
